import {
  MEDIA_CHUNK_BYTES, MEDIA_MAX_PLAINTEXT_BYTES, NOTEBOOK_TEXT_MAX_BYTES, isMediaManifest,
  type MediaCreateRequest, type MediaKind, type MediaManifest, type MediaMode,
} from "@echopaste/protocol";
import {
  base64UrlToUint8Array, decryptMediaChunk, decryptMediaManifest, encryptMediaChunk, encryptMediaManifest,
  generateKey, sha256Base64Url, uint8ArrayToBase64Url, unwrapMediaContentKey, wrapMediaContentKey,
} from "@echopaste/crypto";
import { signedProductionFetch } from "./productionAuth";
import type { PersonalSession } from "./personalSync";

export interface PrepareMediaInput {
  bytes: Uint8Array;
  kind: MediaKind;
  name?: string;
  mime?: string;
  text?: string;
  mode: MediaMode;
  recipientDeviceId?: string;
  roomId: string;
  roomKey: CryptoKey;
  keyVersion: number;
}

export interface PreparedMediaUpload {
  id: string;
  createRequest: MediaCreateRequest;
  encryptedChunks: Uint8Array[];
}

async function checkedJson(response: Response): Promise<any> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) throw new Error(data.error || `Media request failed (${response.status})`);
  return data;
}

export async function prepareMediaUpload(input: PrepareMediaInput): Promise<PreparedMediaUpload> {
  if (input.bytes.byteLength > MEDIA_MAX_PLAINTEXT_BYTES) throw new Error("文件不能超过 25 MiB。");
  if (input.mode === "notebook_delivery" && !input.recipientDeviceId) throw new Error("尚未配置 Notebook 接收端。");
  const inlineNotebookText = input.mode === "notebook_delivery" && input.kind === "text";
  if (inlineNotebookText && input.bytes.byteLength > NOTEBOOK_TEXT_MAX_BYTES) throw new Error("推送到 Notebook 的文字不能超过 64 KiB。");
  const id = crypto.randomUUID();
  const contentKey = await generateKey();
  const context = { projectId: id, roomId: input.roomId, keyVersion: input.keyVersion };
  const manifest: MediaManifest = { version: 1, kind: input.kind, size: input.bytes.byteLength, sha256: await sha256Base64Url(input.bytes), ...(input.name ? { name: input.name } : {}), ...(input.mime ? { mime: input.mime } : {}), ...(inlineNotebookText ? { text: input.text ?? new TextDecoder().decode(input.bytes) } : {}) };
  const encryptedManifest = await encryptMediaManifest(manifest, contentKey, id);
  const wrapped = await wrapMediaContentKey(contentKey, input.roomKey, context);
  const encryptedChunks: Uint8Array[] = [];
  const chunks = [];
  const count = inlineNotebookText ? 0 : Math.max(1, Math.ceil(input.bytes.byteLength / MEDIA_CHUNK_BYTES));
  for (let index = 0; index < count; index++) {
    const plaintext = input.bytes.slice(index * MEDIA_CHUNK_BYTES, Math.min(input.bytes.byteLength, (index + 1) * MEDIA_CHUNK_BYTES));
    const encrypted = await encryptMediaChunk(plaintext, contentKey, id, index);
    const ciphertext = base64UrlToUint8Array(encrypted.ciphertext);
    encryptedChunks.push(ciphertext);
    chunks.push({ index, ciphertextBytes: ciphertext.byteLength, ciphertextSha256: await sha256Base64Url(ciphertext), nonce: encrypted.nonce });
  }
  return { id, encryptedChunks, createRequest: { id, mode: input.mode, contentClass: inlineNotebookText ? "text" : "media", ...(input.recipientDeviceId ? { recipientDeviceId: input.recipientDeviceId } : {}), keyVersion: input.keyVersion, totalCiphertextBytes: chunks.reduce((sum, chunk) => sum + chunk.ciphertextBytes, 0), chunks, encryptedManifest: encryptedManifest.ciphertext, manifestNonce: encryptedManifest.nonce, wrappedContentKey: wrapped.ciphertext, wrappedContentKeyNonce: wrapped.nonce } };
}

export async function uploadPreparedMedia(apiBase: string, session: PersonalSession, prepared: PreparedMediaUpload, onProgress?: (uploaded: number, total: number) => void): Promise<void> {
  await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: "/api/v2/media", method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(prepared.createRequest) }));
  for (let index = 0; index < prepared.encryptedChunks.length; index++) {
    await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: `/api/v2/media/${prepared.id}/chunks/${index}`, method: "PUT", headers: { "Content-Type": "application/octet-stream" }, body: prepared.encryptedChunks[index] }));
    onProgress?.(index + 1, prepared.encryptedChunks.length);
  }
  await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: `/api/v2/media/${prepared.id}/commit`, method: "POST" }));
}

export async function listMediaSinks(apiBase: string, session: PersonalSession): Promise<Array<{ device_id: string; display_name?: string }>> {
  const data = await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: "/api/v2/media/sinks", method: "GET" }));
  return data.sinks ?? [];
}

export async function listMediaInbox(apiBase: string, session: PersonalSession): Promise<any[]> {
  const data = await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: "/api/v2/media/inbox", method: "GET" }));
  return data.items ?? [];
}

export async function claimNotebookMedia(apiBase: string, session: PersonalSession, id: string): Promise<{ claimToken: string; claimExpiresAt: string }> {
  return checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: `/api/v2/media/${id}/claim`, method: "POST" }));
}

export async function downloadEncryptedChunks(apiBase: string, session: PersonalSession, item: MediaCreateRequest, claimToken?: string): Promise<Uint8Array[]> {
  const chunks: Uint8Array[] = [];
  for (const descriptor of item.chunks) {
    const response = await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: `/api/v2/media/${item.id}/chunks/${descriptor.index}`, method: "GET", ...(claimToken ? { headers: { "X-EchoPaste-Claim-Token": claimToken } } : {}) });
    if (!response.ok) throw new Error(`Media download failed (${response.status})`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.byteLength !== descriptor.ciphertextBytes || await sha256Base64Url(bytes) !== descriptor.ciphertextSha256) throw new Error("Ciphertext digest mismatch");
    chunks.push(bytes);
  }
  return chunks;
}

export async function acknowledgeNotebookMedia(apiBase: string, session: PersonalSession, id: string, claimToken?: string): Promise<void> {
  await checkedJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: `/api/v2/media/${id}/ack`, method: "POST", ...(claimToken ? { headers: { "X-EchoPaste-Claim-Token": claimToken } } : {}) }));
}

export async function decryptDownloadedMedia(input: { item: MediaCreateRequest; encryptedChunks: Uint8Array[]; roomId: string; roomKey: CryptoKey }): Promise<{ manifest: MediaManifest; bytes: Uint8Array }> {
  const context = { projectId: input.item.id, roomId: input.roomId, keyVersion: input.item.keyVersion };
  const contentKey = await unwrapMediaContentKey({ ciphertext: input.item.wrappedContentKey, nonce: input.item.wrappedContentKeyNonce }, input.roomKey, context);
  const manifest = await decryptMediaManifest<MediaManifest>({ ciphertext: input.item.encryptedManifest, nonce: input.item.manifestNonce }, contentKey, input.item.id);
  if (!isMediaManifest(manifest)) throw new Error("Invalid encrypted media manifest");
  if (input.item.chunks.length === 0) {
    if (input.item.mode !== "notebook_delivery" || manifest.kind !== "text" || typeof manifest.text !== "string") throw new Error("Invalid inline notebook text");
    const bytes = new TextEncoder().encode(manifest.text);
    if (bytes.byteLength !== manifest.size || await sha256Base64Url(bytes) !== manifest.sha256) throw new Error("Plaintext digest mismatch");
    return { manifest, bytes };
  }
  const parts = await Promise.all(input.encryptedChunks.map(async (chunk, index) => {
    if (await sha256Base64Url(chunk) !== input.item.chunks[index]?.ciphertextSha256) throw new Error("Ciphertext digest mismatch");
    const descriptor = input.item.chunks[index];
    return decryptMediaChunk({ ciphertext: uint8ArrayToBase64Url(chunk), nonce: descriptor.nonce }, contentKey, input.item.id, index);
  }));
  const bytes = new Uint8Array(parts.reduce((sum, part) => sum + part.byteLength, 0));
  let offset = 0; for (const part of parts) { bytes.set(part, offset); offset += part.byteLength; }
  if (bytes.byteLength !== manifest.size || await sha256Base64Url(bytes) !== manifest.sha256) throw new Error("Plaintext digest mismatch");
  return { manifest, bytes };
}
